import { useState, useEffect } from 'preact/hooks';
import { isGeminiModalOpenSignal, showToast } from '../../state/ui.store';
import { apiFetch } from '../../services/api';

export function GeminiKeyModal() {
  const isOpen = isGeminiModalOpenSignal.value;
  const [provider, setProvider] = useState<'litellm' | 'aistudio'>('litellm');
  const [geminiKey, setGeminiKey] = useState('');
  const [geminiHeavyModel, setGeminiHeavyModel] = useState('gemini-3.8-flash');
  const [geminiMediaModel, setGeminiMediaModel] = useState('gemini-3.5-flash-lite');
  const [litellmKey, setLitellmKey] = useState('');
  const [litellmBaseUrl, setLitellmBaseUrl] = useState('https://llm.potencial.tec.br/v1');
  const [litellmHeavyModel, setLitellmHeavyModel] = useState('gemini-3.8-flash-high');
  const [litellmMediaModel, setLitellmMediaModel] = useState('gemini-3.8-flash-low');
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [showLiteLLMKey, setShowLiteLLMKey] = useState(false);
  const [statusGemini, setStatusGemini] = useState('');
  const [statusLiteLLM, setStatusLiteLLM] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAiConfig();
    }
  }, [isOpen]);

  const loadAiConfig = async () => {
    try {
      const data = await apiFetch<{
        activeProvider?: 'litellm' | 'aistudio';
        hasGeminiKey?: boolean;
        maskedGeminiKey?: string;
        geminiHeavyModel?: string;
        geminiMediaModel?: string;
        hasLiteLLMKey?: boolean;
        maskedLiteLLMKey?: string;
        litellmBaseUrl?: string;
        litellmHeavyModel?: string;
        litellmMediaModel?: string;
      }>('/settings/ai-config');

      if (data) {
        if (data.activeProvider) {
          setProvider(data.activeProvider);
        }
        if (data.litellmBaseUrl) {
          setLitellmBaseUrl(data.litellmBaseUrl);
        }
        if (data.litellmHeavyModel) {
          setLitellmHeavyModel(data.litellmHeavyModel);
        }
        if (data.litellmMediaModel) {
          setLitellmMediaModel(data.litellmMediaModel);
        }
        if (data.geminiHeavyModel) {
          setGeminiHeavyModel(data.geminiHeavyModel);
        }
        if (data.geminiMediaModel) {
          setGeminiMediaModel(data.geminiMediaModel);
        }
        setStatusGemini(data.hasGeminiKey ? `Chave salva: ${data.maskedGeminiKey}` : 'Nenhuma chave Gemini salva.');
        setStatusLiteLLM(data.hasLiteLLMKey ? `Chave salva: ${data.maskedLiteLLMKey}` : 'Padrão do servidor configurado.');
      }
    } catch (err: any) {
      // Fallback para rota antiga se necessário
      try {
        const legacy = await apiFetch<{ hasKey: boolean; maskedKey?: string }>('/settings/gemini-key');
        if (legacy && legacy.hasKey) {
          setStatusGemini(`Chave Gemini salva: ${legacy.maskedKey || '••••••••'}`);
        }
      } catch (e) {}
    }
  };

  const handleSave = async (e: Event) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload: any = {
        provider,
        litellmBaseUrl: litellmBaseUrl.trim(),
        litellmHeavyModel: litellmHeavyModel.trim(),
        litellmMediaModel: litellmMediaModel.trim(),
        geminiHeavyModel: geminiHeavyModel.trim(),
        geminiMediaModel: geminiMediaModel.trim()
      };

      if (litellmKey.trim()) {
        payload.litellmApiKey = litellmKey.trim();
      }

      if (geminiKey.trim()) {
        payload.geminiApiKey = geminiKey.trim();
        payload.apiKey = geminiKey.trim();
        payload.geminiKey = geminiKey.trim();
      }

      const res = await apiFetch<{ success: boolean; message?: string }>('/settings/ai-config', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      showToast(res?.message || 'Configurações de IA salvas com sucesso no banco de dados!', 'success');
      isGeminiModalOpenSignal.value = false;
      setGeminiKey('');
      setLitellmKey('');
    } catch (err: any) {
      showToast(`Erro ao salvar configurações de IA: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-fade-in">
      <div
        class="w-full max-w-lg p-6 bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl space-y-5"
        style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}
      >
        <div class="flex items-center justify-between border-b border-slate-800 pb-3">
          <h3 class="text-sm font-bold text-slate-100 flex items-center gap-2">
            🤖 Provedor de Inteligência Artificial & Chaves
          </h3>
          <button
            onClick={() => isGeminiModalOpenSignal.value = false}
            class="text-slate-400 hover:text-white p-1 cursor-pointer"
          >
            ✕
          </button>
        </div>

        {/* Seletor de Provedor de IA */}
        <div class="space-y-2">
          <label class="block text-xs font-semibold text-slate-300">
            Selecione o Provedor de IA Ativo:
          </label>
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setProvider('litellm')}
              class={`p-3 rounded-2xl border text-left cursor-pointer transition flex flex-col gap-1 ${
                provider === 'litellm'
                  ? 'bg-cyan-500/15 border-cyan-500/50 text-cyan-200'
                  : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
              }`}
            >
              <div class="flex items-center justify-between w-full">
                <span class="text-xs font-bold text-slate-100 flex items-center gap-1.5">
                  ⚡ LiteLLM Potencial
                </span>
                {provider === 'litellm' && <span class="text-[10px] bg-cyan-500/30 text-cyan-300 px-1.5 py-0.5 rounded-full font-bold">Ativo</span>}
              </div>
              <span class="text-[11px] opacity-80">
                gemini-3.8-flash-high (Resumos pesados e Alfred)
              </span>
            </button>

            <button
              type="button"
              onClick={() => setProvider('aistudio')}
              class={`p-3 rounded-2xl border text-left cursor-pointer transition flex flex-col gap-1 ${
                provider === 'aistudio'
                  ? 'bg-cyan-500/15 border-cyan-500/50 text-cyan-200'
                  : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
              }`}
            >
              <div class="flex items-center justify-between w-full">
                <span class="text-xs font-bold text-slate-100 flex items-center gap-1.5">
                  🌐 Google AI Studio
                </span>
                {provider === 'aistudio' && <span class="text-[10px] bg-cyan-500/30 text-cyan-300 px-1.5 py-0.5 rounded-full font-bold">Ativo</span>}
              </div>
              <span class="text-[11px] opacity-80">
                API Direta do Google Gemini
              </span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSave} class="space-y-4">
          {provider === 'litellm' ? (
            <div class="space-y-3 p-3.5 bg-slate-950/70 border border-slate-800/80 rounded-2xl">
              <div class="text-[11px] text-cyan-300 flex items-center justify-between">
                <span class="font-bold flex items-center gap-1.5">⚡ Configuração do LiteLLM</span>
                <span class="text-[10px] text-slate-400">Salvo no Banco de Dados</span>
              </div>

              <div>
                <label class="block text-xs font-semibold text-slate-300 mb-1 flex items-center justify-between">
                  <span>Chave de API LiteLLM (`LITELLM_API_KEY`)</span>
                  <span class="text-[10px] text-slate-400 font-mono">{statusLiteLLM}</span>
                </label>
                <div class="relative">
                  <input
                    type={showLiteLLMKey ? 'text' : 'password'}
                    placeholder="Cole sua chave sk-litellm-..."
                    value={litellmKey}
                    onInput={(e) => setLitellmKey((e.target as HTMLInputElement).value)}
                    class="w-full pl-3.5 pr-10 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLiteLLMKey(!showLiteLLMKey)}
                    class="absolute right-3 top-2 text-slate-400 hover:text-white cursor-pointer"
                  >
                    {showLiteLLMKey ? '🔒' : '👁️'}
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-slate-300 mb-1">
                  URL do Serviço LiteLLM
                </label>
                <input
                  type="text"
                  value={litellmBaseUrl}
                  onInput={(e) => setLitellmBaseUrl((e.target as HTMLInputElement).value)}
                  placeholder="https://llm.potencial.tec.br/v1"
                  class="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 font-mono"
                />
              </div>

              <div class="grid grid-cols-2 gap-2.5 pt-1">
                <div>
                  <label class="block text-[11px] font-semibold text-slate-300 mb-0.5">
                    🧠 Modelo Pesado (Resumos & Chat)
                  </label>
                  <p class="text-[10px] text-slate-400 mb-1 leading-tight">Síntese executiva e RAG</p>
                  <input
                    type="text"
                    list="litellm-heavy-options"
                    value={litellmHeavyModel}
                    onInput={(e) => setLitellmHeavyModel((e.target as HTMLInputElement).value)}
                    placeholder="gemini-3.8-flash-high"
                    class="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 font-mono"
                  />
                  <datalist id="litellm-heavy-options">
                    <option value="gemini-3.8-flash-high" />
                    <option value="gemini-3.8-flash-medium" />
                    <option value="gemini-3.7-flash-high" />
                  </datalist>
                </div>

                <div>
                  <label class="block text-[11px] font-semibold text-slate-300 mb-0.5">
                    🎙️ Áudio & Imagem (Transcrição & Visão)
                  </label>
                  <p class="text-[10px] text-slate-400 mb-1 leading-tight">Áudios e fotos com baixa latência</p>
                  <input
                    type="text"
                    list="litellm-media-options"
                    value={litellmMediaModel}
                    onInput={(e) => setLitellmMediaModel((e.target as HTMLInputElement).value)}
                    placeholder="gemini-3.8-flash-low"
                    class="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 font-mono"
                  />
                  <datalist id="litellm-media-options">
                    <option value="gemini-3.8-flash-low" />
                    <option value="gemini-3.7-flash-low" />
                    <option value="gemini-3.8-flash-medium" />
                  </datalist>
                </div>
              </div>
            </div>
          ) : (
            <div class="space-y-3 p-3.5 bg-slate-950/70 border border-slate-800/80 rounded-2xl">
              <div class="flex items-center justify-between">
                <span class="text-[11px] text-cyan-300 font-bold flex items-center gap-1.5">
                  🌐 Configuração Google Gemini AI Studio
                </span>
                <a
                  href="https://aistudio.google.com/app/apikey"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-[10px] text-cyan-400 hover:underline inline-flex items-center gap-1"
                >
                  Obter chave gratuita ↗
                </a>
              </div>

              <div>
                <label class="block text-xs font-semibold text-slate-300 mb-1 flex items-center justify-between">
                  <span>Chave Gemini (`GEMINI_API_KEY`)</span>
                  <span class="text-[10px] text-slate-400 font-mono">{statusGemini}</span>
                </label>
                <div class="relative">
                  <input
                    type={showGeminiKey ? 'text' : 'password'}
                    placeholder="Cole sua chave AIzaSy..."
                    value={geminiKey}
                    onInput={(e) => setGeminiKey((e.target as HTMLInputElement).value)}
                    class="w-full pl-3.5 pr-10 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowGeminiKey(!showGeminiKey)}
                    class="absolute right-3 top-2 text-slate-400 hover:text-white cursor-pointer"
                  >
                    {showGeminiKey ? '🔒' : '👁️'}
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2.5 pt-1">
                <div>
                  <label class="block text-[11px] font-semibold text-slate-300 mb-0.5">
                    🧠 Modelo Pesado (Resumos & Chat)
                  </label>
                  <p class="text-[10px] text-slate-400 mb-1 leading-tight">Modelo principal de síntese</p>
                  <input
                    type="text"
                    list="gemini-heavy-options"
                    value={geminiHeavyModel}
                    onInput={(e) => setGeminiHeavyModel((e.target as HTMLInputElement).value)}
                    placeholder="gemini-3.8-flash"
                    class="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 font-mono"
                  />
                  <datalist id="gemini-heavy-options">
                    <option value="gemini-3.8-flash" />
                    <option value="gemini-3.7-flash" />
                    <option value="gemini-3.6-flash" />
                    <option value="gemini-3.5-flash" />
                  </datalist>
                </div>

                <div>
                  <label class="block text-[11px] font-semibold text-slate-300 mb-0.5">
                    🎙️ Áudio & Imagem (Transcrição & Visão)
                  </label>
                  <p class="text-[10px] text-slate-400 mb-1 leading-tight">Modelo rápido para mídias</p>
                  <input
                    type="text"
                    list="gemini-media-options"
                    value={geminiMediaModel}
                    onInput={(e) => setGeminiMediaModel((e.target as HTMLInputElement).value)}
                    placeholder="gemini-3.5-flash-lite"
                    class="w-full px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 font-mono"
                  />
                  <datalist id="gemini-media-options">
                    <option value="gemini-3.5-flash-lite" />
                    <option value="gemini-3.1-flash-lite" />
                    <option value="gemini-2.5-flash" />
                  </datalist>
                </div>
              </div>
            </div>
          )}

          <div class="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-2xl text-[11px] text-cyan-200 leading-relaxed">
            <p>
              💡 <strong>Persistência no Banco & Fallback:</strong> Todas as configurações salvas aqui têm prioridade sobre o ambiente e persistem no banco de dados. O Alfred utiliza automaticamente o outro provedor caso o selecionado sofra oscilações.
            </p>
          </div>

          <div class="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => isGeminiModalOpenSignal.value = false}
              class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              class="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-xs font-bold shadow-md shadow-cyan-600/20 cursor-pointer transition flex items-center gap-1.5 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
